import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { siteTypeEnum } from '../../constants/enums/site';
import { useBoolean } from '../../hooks/useBoolean';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { Site } from '../../services/apiSLTP/sites/sites';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import ModalBody from '../modals/ModalBody';
import CreateActivityModal from '../modals/operations/planning/CreateActivityModal';
import IconButton from '../ui/IconButton';

interface SiteCellProps {
    activity: DataItem;
    disabled?: boolean;
    refetch?: () => void;
    currentDate: Date;
    displayAll?: boolean;
    remainingMaterial?: boolean;
    activityState?: number;
    reportState?: number;
    stepMode?: number;
    previousSiteId?: string | null;
    nextSiteId?: string | null;
    activityIndex?: number | null;
}

const SiteCell: React.FC<SiteCellProps> = React.memo(
    ({
        activity,
        disabled = false,
        refetch = () => {},
        currentDate,
        displayAll = false,
        remainingMaterial = false,
        activityState,
        reportState,
        stepMode,
        nextSiteId,
        previousSiteId,
    }) => {
        const [selectedAddress, setSelectedAddress] = useState<Site | null>(null);
        const addressModal = useBoolean(false);
        const activityModal = useBoolean(false);

        const { t } = useTranslation();

        const setSpecificHour = () => {
            const date = new Date(currentDate);
            date.setHours(7, 0);
            return date.toISOString();
        };

        const isFirstActivityInSite = () => {
            return previousSiteId !== activity.siteId._id;
        };

        const openMaps = (address: string | undefined) => {
            if (!address) return;
            const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}`;
            window.open(mapsUrl, '_blank', 'noopener,noreferrer');
        };

        return (
            <div title={activity.siteId.name + ' ' + formatName(activity.siteId.conducId?.firstName, activity.siteId.conducId?.lastName)}>
                <div
                    className="font-w-600 fx-r gap-5px fx-items-bl"
                    style={{ color: isFirstActivityInSite() || displayAll ? undefined : '#666666' }}
                    title={activity.siteId.name}
                >
                    {activity.siteId.type === siteTypeEnum.TRAINING && (
                        <div
                            className="text-black fx-r fx-items-c fx-justify-c"
                            style={{ backgroundColor: 'gold', borderRadius: 10, width: 20, height: 20 }}
                        >
                            {t('training').charAt(0).toUpperCase()}
                        </div>
                    )}
                    {activity.siteId.type === siteTypeEnum.EXTERNAL && (
                        <div
                            className="text-black fx-r fx-items-c fx-justify-c"
                            style={{ backgroundColor: 'darkviolet', borderRadius: 10, width: 20, height: 20 }}
                        >
                            {t('external').charAt(0).toUpperCase()}
                        </div>
                    )}
                    {activity.siteId.name.includes(' - ') ? activity.siteId.name.split(' - ').slice(1).join(' - ') || '' : activity.siteId.name}
                </div>

                {(isFirstActivityInSite() || displayAll) && (
                    <>
                        <div className="fx-items-c fx-r">
                            {!!activity.siteId.address && (
                                <IconButton
                                    iconName="infoSharp"
                                    onClick={() => {
                                        setSelectedAddress(activity.siteId);
                                        addressModal.setTrue();
                                    }}
                                    title={capitalizeFirstLetter(t('siteInformations'))}
                                />
                            )}

                            {!disabled && (
                                <IconButton
                                    iconName="addCircle"
                                    onClick={() => {
                                        activityModal.setTrue();
                                    }}
                                    title={capitalizeFirstLetter(t('add'))}
                                />
                            )}

                            <div className="pad-l-5px one-line">
                                {formatName(activity.siteId.conducId?.firstName, activity.siteId.conducId?.lastName)}
                            </div>
                        </div>
                    </>
                )}
                {addressModal.value && (
                    <ModalBody
                        open={addressModal.value}
                        handleClose={() => addressModal.setFalse()}
                        showSubmitButton={false}
                        title={t('site')}
                        subtitle={t('infos')}
                        description={selectedAddress?.name}
                    >
                        <div className="fx-r fx-items-c">
                            <IconButton iconName="locationOnSharp" onClick={() => openMaps(selectedAddress?.address)} />
                            {selectedAddress?.address}
                        </div>
                    </ModalBody>
                )}
                {activityModal.value && (
                    <CreateActivityModal
                        open={activityModal.value}
                        handleClose={() => activityModal.setFalse()}
                        initDate={setSpecificHour()}
                        refetch={() => refetch()}
                        initSiteId={activity.siteId._id}
                        currentDate={currentDate}
                        activityState={activityState}
                        reportState={reportState}
                        stepMode={stepMode}
                    />
                )}
            </div>
        );
    }
);

export default SiteCell;
